class LyleProductFeatures extends HTMLElement {
  #activeIndex = 0;
  #hasActivated = false;
  #contentVisible = false;
  #mediaQuery = window.matchMedia('(max-width: 1023px)');
  #observer = null;
  #onMediaChange = null;
  #onSectionEnter = null;
  #onPointerOver = null;
  #onClick = null;
  #onFocusIn = null;

  connectedCallback() {
    // Liquid picks the first feature that has copy as the initial active one, so
    // the opening view never shows an image/highlight with no matching text.
    this.#activeIndex = Number(this.dataset.initialIndex) || 0;
    this.#hasActivated = this.classList.contains('is-activated');
    this.#contentVisible = this.classList.contains('is-content-visible');
    this.#setup();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#onMediaChange) {
      this.#mediaQuery.removeEventListener('change', this.#onMediaChange);
      this.#onMediaChange = null;
    }
    this.removeEventListener('mouseenter', this.#onSectionEnter);
    this.removeEventListener('mouseover', this.#onPointerOver);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.#onSectionEnter = null;
    this.#onPointerOver = null;
    this.#onClick = null;
    this.#onFocusIn = null;
  }

  #setup() {
    this.#updateActive();

    this.#onSectionEnter = () => {
      if (!this.#mediaQuery.matches) this.#activate();
    };
    this.addEventListener('mouseenter', this.#onSectionEnter);

    // Delegate highlight hover/click on the host so the listeners are removable.
    this.#onPointerOver = (event) => {
      if (this.#mediaQuery.matches) return;
      const btn = event.target.closest('[data-highlight-index]');
      if (btn) this.#setIndex(Number(btn.dataset.highlightIndex));
    };
    this.addEventListener('mouseover', this.#onPointerOver);

    this.#onClick = (event) => {
      const btn = event.target.closest('[data-highlight-index]');
      if (btn) this.#setIndex(Number(btn.dataset.highlightIndex));
    };
    this.addEventListener('click', this.#onClick);

    // Keyboard reveal + sync: tabbing a highlight into focus activates the section
    // (so copy is never stuck hidden for keyboard-only desktop users) AND switches
    // to that feature, matching the hover/click paths. Reveal without the index
    // sync would move focus while the image/copy stay on feature 0.
    this.#onFocusIn = (event) => {
      this.#activate();
      const btn = event.target.closest('[data-highlight-index]');
      if (btn) this.#setIndex(Number(btn.dataset.highlightIndex));
    };
    this.addEventListener('focusin', this.#onFocusIn);

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && this.#mediaQuery.matches) {
          this.#activate();
          this.#observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    this.#observer.observe(this);

    // Re-check activation when crossing the mobile breakpoint: a section that was
    // already in view on desktop won't fire a fresh intersection event on resize.
    this.#onMediaChange = (event) => {
      if (event.matches && !this.#hasActivated) {
        const rect = this.getBoundingClientRect();
        const visible = rect.top < window.innerHeight && rect.bottom > 0;
        if (visible) {
          this.#activate();
          this.#observer?.disconnect();
        }
      }
    };
    this.#mediaQuery.addEventListener('change', this.#onMediaChange);
  }

  #activate() {
    if (this.#hasActivated) return;
    this.#hasActivated = true;
    this.classList.add('is-activated');
    setTimeout(() => {
      this.#contentVisible = true;
      this.classList.add('is-content-visible');
      // Now that copy is visible, take the active panel out of inert so its
      // links become reachable (and keep the hidden ones inert).
      this.#updateActive();
    }, 600);
  }

  #setIndex(index) {
    if (this.#activeIndex === index) return;
    this.#activeIndex = index;
    this.#updateActive();
  }

  #updateActive() {
    const index = this.#activeIndex;

    for (const el of this.querySelectorAll('[data-highlight-index]')) {
      const isActive = Number(el.dataset.highlightIndex) === index;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
    for (const el of this.querySelectorAll('[data-content-index]')) {
      const isActive = Number(el.dataset.contentIndex) === index;
      el.classList.toggle('is-active', isActive);
      // Keep hidden copy (and all copy before the reveal) out of the tab order so
      // links inside non-active richtext descriptions aren't focusable.
      el.inert = !(isActive && this.#contentVisible);
    }
    for (const el of this.querySelectorAll('[data-image-index]')) {
      el.classList.toggle('is-active', Number(el.dataset.imageIndex) === index);
    }
  }
}

if (!customElements.get('lyle-product-features')) {
  customElements.define('lyle-product-features', LyleProductFeatures);
}
